import React from 'react';
import { Button, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PhoneIcon from '@material-ui/icons/Phone';
import SmsIcon from '@material-ui/icons/Sms';
import ScheduleIcon from '@material-ui/icons/Schedule';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import Section from './Section';
import Reveal from './Reveal';
import RequestQuoteForm from './RequestQuoteForm';
import siteConfig, { services } from '../siteConfig';
import { colors } from '../theme';

const useStyles = makeStyles((theme) => ({
  info: {
    height: '100%',
    backgroundColor: colors.navy,
    color: '#FFFFFF',
    borderRadius: 16,
    padding: theme.spacing(5),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(4, 3),
    },
  },
  infoTitle: {
    marginBottom: theme.spacing(1),
  },
  infoIntro: {
    color: 'rgba(255,255,255,0.8)',
    marginBottom: theme.spacing(4),
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  rowIcon: {
    flexShrink: 0,
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.08)',
    color: colors.gold,
  },
  rowLabel: {
    fontSize: 13,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: 'rgba(255,255,255,0.6)',
    fontWeight: 600,
  },
  rowValue: {
    fontFamily: theme.typography.h6.fontFamily,
    fontWeight: 700,
    fontSize: 18,
    color: '#FFFFFF',
    textDecoration: 'none',
  },
  buttons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  textButton: {
    color: '#FFFFFF',
    borderColor: 'rgba(255,255,255,0.5)',
  },
  formCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: theme.spacing(5),
    border: '1px solid rgba(14,35,64,0.08)',
    boxShadow: '0 12px 40px rgba(14,35,64,0.08)',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3),
    },
  },
  formTitle: {
    color: colors.navy,
    marginBottom: theme.spacing(3),
  },
}));

const formServices = [...services.map((service) => service.title), 'Other / not sure'];

export default function ContactSection() {
  const classes = useStyles();

  const rows = [
    {
      icon: PhoneIcon,
      label: 'Call or text',
      value: siteConfig.phoneDisplay,
      href: siteConfig.phoneHref,
    },
    { icon: ScheduleIcon, label: 'Availability', value: 'Any time' },
    siteConfig.serviceArea && {
      icon: LocationOnIcon,
      label: 'Service area',
      value: siteConfig.serviceArea.replace(/^the /, '').replace(/^\w/, (c) => c.toUpperCase()),
    },
    { icon: CheckCircleIcon, label: 'Estimates', value: 'Always free' },
  ].filter(Boolean);

  return (
    <Section
      id="contact"
      eyebrow="Free estimate"
      title="Let's talk about your project"
      subtitle="Send us a few details and we'll get back to you with a free, no-obligation estimate."
    >
      <Grid container spacing={4}>
        <Grid item xs={12} md={5}>
          <Reveal>
            <div className={classes.info}>
              <Typography variant="h5" component="h3" className={classes.infoTitle}>
                Prefer to talk?
              </Typography>
              <Typography className={classes.infoIntro}>
                Give us a call or send a text — we're happy to answer questions.
              </Typography>
              {rows.map(({ icon: Icon, label, value, href }) => (
                <div className={classes.row} key={label}>
                  <span className={classes.rowIcon}>
                    <Icon />
                  </span>
                  <div>
                    <div className={classes.rowLabel}>{label}</div>
                    {href ? (
                      <a href={href} className={classes.rowValue}>
                        {value}
                      </a>
                    ) : (
                      <div className={classes.rowValue}>{value}</div>
                    )}
                  </div>
                </div>
              ))}
              <div className={classes.buttons}>
                <Button variant="contained" color="secondary" href={siteConfig.phoneHref} startIcon={<PhoneIcon />}>
                  Call now
                </Button>
                <Button variant="outlined" href={siteConfig.smsHref} startIcon={<SmsIcon />} className={classes.textButton}>
                  Send a text
                </Button>
              </div>
            </div>
          </Reveal>
        </Grid>
        <Grid item xs={12} md={7}>
          <Reveal delay={100}>
            <div className={classes.formCard}>
              <Typography variant="h5" component="h3" className={classes.formTitle}>
                Request a free estimate
              </Typography>
              <RequestQuoteForm services={formServices} />
            </div>
          </Reveal>
        </Grid>
      </Grid>
    </Section>
  );
}
